/**
 * Dossiers' writes, one function per intent. Each names the `version` of the
 * case on screen and carries the intent's Idempotency-Key (ADR-0016 §2.1).
 * Nothing is applied to the screen until the server answers: the caller shows
 * the case the response carries.
 */
import type { SupportCaseDetail, SupportCasePriority } from "@wallyo/contracts/console/cases";
import type { ConsoleClient } from "../../api/client";
import {
  addCaseNote,
  claimSupportCase,
  closeSupportCase,
  linkCaseCustomer,
  logCaseCall,
  openSupportCase,
  reassignSupportCase,
  replyToCase,
} from "../../api/console-api";
import { ConsoleApiError } from "../../api/errors";
import { describeError } from "../../auth/error-message";
import { t, type MessageId } from "../../i18n";

/** The server's bounds (server/http/validation/cases.ts). */
export const REASON_MIN_LENGTH = 3;
export const SUBJECT_MIN_LENGTH = 3;
export const ENTRY_MAX_LENGTH = 4000;
export const CALL_MAX_MINUTES = 24 * 60;
const E164 = /^\+[0-9]{8,15}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function reasonProblem(reason: string): string | null {
  return reason.trim().length >= REASON_MIN_LENGTH ? null : t("cases.problem.reason");
}

/** What a reply, note or call summary says. */
export function bodyProblem(body: string): string | null {
  const text = body.trim();
  if (!text) return t("cases.problem.body");
  if (text.length > ENTRY_MAX_LENGTH) return t("cases.problem.tooLong", { max: ENTRY_MAX_LENGTH });
  return null;
}

/** A call's length in minutes, as typed; "" is "not given". */
export function minutesProblem(minutes: string): string | null {
  if (minutes.trim() === "") return null;
  const value = Number(minutes);
  return Number.isInteger(value) && value >= 0 && value <= CALL_MAX_MINUTES
    ? null
    : t("cases.problem.minutes", { max: CALL_MAX_MINUTES });
}

const toSeconds = (minutes: string): number | null => (minutes.trim() === "" ? null : Number(minutes) * 60);

/** "+224 621 55 09 12" → "+224621550912", as the server reads it. */
export const normalisePhone = (phone: string) => phone.trim().replace(/[\s-]/g, "");

export function phoneProblem(phone: string): string | null {
  return E164.test(normalisePhone(phone)) ? null : t("cases.problem.phone");
}

export function idProblem(id: string, message: "cases.problem.staffId" | "cases.problem.customerId"): string | null {
  return UUID.test(id.trim()) ? null : t(message);
}

export function sendClaim(client: ConsoleClient, detail: SupportCaseDetail, idempotencyKey: string) {
  return claimSupportCase(client, detail.supportCaseId, { version: detail.version }, { idempotencyKey });
}

export function sendReassign(
  client: ConsoleClient,
  detail: SupportCaseDetail,
  change: { toStaffMemberId: string; reason: string },
  idempotencyKey: string,
) {
  return reassignSupportCase(
    client,
    detail.supportCaseId,
    { toStaffMemberId: change.toStaffMemberId.trim(), version: detail.version, reason: change.reason.trim() },
    { idempotencyKey },
  );
}

export function sendNote(client: ConsoleClient, detail: SupportCaseDetail, body: string, idempotencyKey: string) {
  return addCaseNote(client, detail.supportCaseId, { body: body.trim(), version: detail.version }, { idempotencyKey });
}

/** A reply in the app: the server notifies the customer. Never by SMS. */
export function sendReply(client: ConsoleClient, detail: SupportCaseDetail, body: string, idempotencyKey: string) {
  return replyToCase(client, detail.supportCaseId, { body: body.trim(), version: detail.version }, { idempotencyKey });
}

export function sendCall(
  client: ConsoleClient,
  detail: SupportCaseDetail,
  call: { body: string; minutes: string },
  idempotencyKey: string,
) {
  return logCaseCall(
    client,
    detail.supportCaseId,
    { body: call.body.trim(), callDurationSeconds: toSeconds(call.minutes), version: detail.version },
    { idempotencyKey },
  );
}

export function sendClose(client: ConsoleClient, detail: SupportCaseDetail, reason: string, idempotencyKey: string) {
  return closeSupportCase(
    client,
    detail.supportCaseId,
    { reason: reason.trim(), version: detail.version },
    { idempotencyKey },
  );
}

export function sendLink(
  client: ConsoleClient,
  detail: SupportCaseDetail,
  link: { userId: string; reason: string },
  idempotencyKey: string,
) {
  return linkCaseCustomer(
    client,
    detail.supportCaseId,
    { userId: link.userId.trim(), version: detail.version, reason: link.reason.trim() },
    { idempotencyKey },
  );
}

/** « + Noter un appel »: who called (a customer, or an unknown caller's number), and what was said. */
export type NewCallFields = {
  caller: "customer" | "unknown";
  userId: string;
  phone: string;
  subject: string;
  body: string;
  minutes: string;
  priority: SupportCasePriority;
};

export const EMPTY_CALL: NewCallFields = {
  caller: "customer",
  userId: "",
  phone: "",
  subject: "",
  body: "",
  minutes: "",
  priority: "normal",
};

export function newCallProblem(fields: NewCallFields): string | null {
  if (fields.caller === "customer" && !fields.userId.trim()) return t("cases.problem.pickCustomer");
  if (fields.caller === "customer") {
    const bad = idProblem(fields.userId, "cases.problem.customerId");
    if (bad) return bad;
  }
  if (fields.caller === "unknown") {
    const bad = phoneProblem(fields.phone);
    if (bad) return bad;
  }
  if (fields.subject.trim().length < SUBJECT_MIN_LENGTH) return t("cases.problem.subject");
  return bodyProblem(fields.body) ?? minutesProblem(fields.minutes);
}

export function sendOpenCase(client: ConsoleClient, fields: NewCallFields, idempotencyKey: string) {
  return openSupportCase(
    client,
    {
      subject: fields.subject.trim(),
      body: fields.body.trim(),
      callDurationSeconds: toSeconds(fields.minutes),
      userId: fields.caller === "customer" ? fields.userId.trim() : null,
      contactPhone: fields.caller === "unknown" ? normalisePhone(fields.phone) : null,
      priority: fields.priority,
    },
    { idempotencyKey },
  );
}

export type CaseFailure = {
  message: string;
  reference: string;
  /** `stale_version`: the case changed meanwhile. Offer a reload. */
  stale: boolean;
};

/** The server's reasons a case write is refused for (ConsoleErrorReason), in the staff member's words. */
const REFUSALS: Readonly<Record<string, MessageId>> = {
  already_claimed: "cases.refused.claimed",
  case_closed: "cases.refused.closed",
  not_case_owner: "cases.refused.notOwner",
  no_customer: "cases.refused.noCustomer",
};

/**
 * How a failed write reads (ADR-0016 §2.5), from the server's `reason`: a
 * claim lost to someone else names who won (from the case read again), a
 * stale version offers a reload, and a known refusal says what to do. Any
 * other failure is translated by its code, with the request ID; never the
 * server's English.
 */
export function describeCaseFailure(error: unknown, claimedBy: string | null = null): CaseFailure {
  const { message, reference } = describeError(error);
  if (!(error instanceof ConsoleApiError)) return { message, reference, stale: false };
  if (claimedBy) return { message: t("cases.claim.taken", { name: claimedBy }), reference, stale: false };
  if (error.reason === "stale_version" || error.code === "stale_version") {
    return { message: t("cases.stale"), reference, stale: true };
  }
  const refusal = error.reason ? REFUSALS[error.reason] : undefined;
  return { message: refusal ? t(refusal) : message, reference, stale: false };
}

/**
 * After a refused claim, the case read again says who won the race: someone
 * else now owns it. Only for `already_claimed` (or, from a server that sends
 * no reason, anything but a stale version); null when the claim was in fact
 * ours or nobody owns it.
 */
export function claimWinner(error: unknown, current: SupportCaseDetail | null, myId: string): string | null {
  if (!(error instanceof ConsoleApiError)) return null;
  if (error.reason ? error.reason !== "already_claimed" : error.code === "stale_version") return null;
  if (!current?.owner || current.owner.staffMemberId === myId) return null;
  return current.owner.name;
}
